import { getApiToken } from '../helpers/helper';

import { BASE_URL, _request } from './request';

export type OrdainedModel = {
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  age_confirmed: boolean;
  minister_requested: boolean;
  principles_required: boolean;
  terms_accepted: boolean;
  hash?: string;
};

export type OrdainedResponseModel = {
  first_name: string;
  middle_name: string;
  last_name: string;
  address: string;
  city: string;
  state: string;
  age_confirmed: boolean;
  minister_requested: boolean;
  terms_accepted: boolean;
  hash: string;
  // PCO fields (added M1)
  shipping_address?: string | null;
  shipping_address2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_zip?: string | null;
  shipping_country?: string | null;
  documentSupportTypes?: string[];
  document_upload_url?: string | null;
  status?: OrdainedStatus | null;
  is_priority?: OrdainedPriority | null;
  editable?: boolean;
  flow?: OrdinationFlow;
};

// Which ordination-documents experience the server resolved for this user:
// 'typeform' = legacy external Typeform link (purchased ordination documents
// before the admin-set cutoff, no synced/in-app submission); 'in_app' = the
// PCO modal flow.
export type OrdinationFlow = 'typeform' | 'in_app';

export const TYPEFORM_ORDINATION_URL =
  'https://8nmzrcg1gp2.typeform.com/to/p8L9wB5J';

// Pipeline status order, low → high:
// Hold (paused) < Pending (default) < Submitted < Printed < Sent.
// 'Refunded' sits outside that order rather than at the end of it. It is
// admin-only, and it makes the minister's submission read-only.
export type OrdainedStatus =
  | 'Hold'
  | 'Pending'
  | 'Submitted'
  | 'Printed'
  | 'Sent'
  | 'Refunded';

// Admin-managed handling priority; default 'Standard'.
export type OrdainedPriority = 'Priority' | 'Standard';

export type SaveOrdainedResponse = {
  success: boolean;
  message: string;
};

export type GetOrdainedResponse = {
  success: boolean;
  message: string;
  data: OrdainedResponseModel;
};

export const saveOrdained = async (
  ordained: OrdainedModel,
  ceremonyId: number
) => {
  const responseBody = await _request<SaveOrdainedResponse>({
    url: `ordained/save`,
    method: 'POST',
    body: {
      ordained: ordained,
      ceremonyId: ceremonyId,
    },
  });
  return responseBody;
};

export const getOrdained = async () => {
  const responseBody = await _request<GetOrdainedResponse>({
    url: `ordained`,
    method: 'GET',
  });
  return responseBody;
};

export type PromoFirstVisitResponse = {
  success: boolean;
  data: { firstVisitAt: string };
};

export const getPromoFirstVisit = async (ceremonyId: string) => {
  const responseBody = await _request<PromoFirstVisitResponse>({
    url: `ordained/promo-first-visit/${ceremonyId}`,
    method: 'GET',
  });
  return responseBody;
};

export const updateOrdained = async ({
  firstName,
  middleName,
  lastName,
}: {
  firstName: string;
  middleName: string;
  lastName: string;
}) => {
  const responseBody = await _request<GetOrdainedResponse>({
    url: `ordained`,
    method: 'PUT',
    body: {
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
    },
  });
  return responseBody;
};

export type UpdateOrdainedPCOPayload = {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  shippingAddress?: string;
  shippingAddress2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingZip?: string;
  shippingCountry?: string;
  documentSupportTypes?: string[];
  documentUploadUrl?: string | null;
};

export const updateOrdainedPCO = async (payload: UpdateOrdainedPCOPayload) => {
  const responseBody = await _request<GetOrdainedResponse>({
    url: `ordained`,
    method: 'PUT',
    body: payload,
  });
  return responseBody;
};

export type UploadFileResponse = {
  success: boolean;
  message?: string;
  data: { url: string };
};

// Uploads a file to S3 via our backend (multipart/form-data). Pass an
// AbortSignal to cancel the in-flight request (e.g. on component unmount).
export const uploadOrdainedFile = async (
  file: File,
  signal?: AbortSignal
): Promise<UploadFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const token = getApiToken();

  const resp = await fetch(`${BASE_URL}ordained/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    // No Content-Type — fetch sets the multipart boundary automatically.
    body: formData,
    signal,
  });

  if (!resp.ok) {
    let msg = `Upload failed (${resp.status})`;
    try {
      const body = await resp.json();
      if (body?.message) msg = body.message;
    } catch {
      // non-JSON error body
    }
    throw new Error(msg);
  }

  return (await resp.json()) as UploadFileResponse;
};
